// ./src/pages/private/EventTicketsPage/TicketDrawer.tsx
//
// Add / edit one ticket type (or add-on). Covers the base ticket: name, an
// admission-vs-add-on toggle, pricing (fixed or pay-what-you-want), how/where it
// sells, inventory + per-order limits, an optional sales window, and whether a
// membership is needed. Variable pricing rules (early-bird / volume / …) are a
// separate, advanced surface and aren't edited here. Uses the shared AppDrawer +
// form fields.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Group, Text, Divider } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { OpResult } from '@sdflc/api-helpers';

import {
  TicketType,
  TicketTypeInput,
  TicketTypeService,
  TICKET_SALES_MODE,
  TICKET_MEMBERSHIP_MODE,
  TICKET_REENTRY,
} from '@/utils/api';
import { useToast, useModalShortcuts, useAddonCategories, useFormSnapshot, SHORTCUT_HINTS } from '@/hooks';
import { getFormErrors, formatErrors, moveEndWithStart, utcToZonedString, zonedStringToUtc } from '@/utils';
import {
  AppDrawer,
  TextField,
  TextAreaField,
  SelectField,
  NumberField,
  SwitchField,
  DateTimeField,
  currencyAffixes,
  TaxSelect,
} from '@/components/generic';
import { eventZoneNote } from '@/components/features/Events';
import { MembershipTypesMultiSelect } from '@/components/features/Memberships';

const FREE = 'free';
const PAID = 'paid';
const PWYW = 'pwyw';
type PriceKind = typeof FREE | typeof PAID | typeof PWYW;



// Server error names that map to a visible input: known → inline under the field,
// anything else → generic, in the alert above Save.
import { useEventTimeSlots } from '@/hooks';
import { bookableSlotOptions, formatSlotLabel } from '@/utils/eventDates';

const KNOWN_FORM_FIELDS = ['name', 'description', 'isAddOn', 'requiresTicket', 'addonCategoryId', 'priceKind', 'price', 'minPrice', 'maxPrice', 'taxSettingId', 'salesMode', 'quantityTotal', 'eventTimeSlotId', 'minPerOrder', 'maxOrderCount', 'orderIncrement', 'salesStartsAt', 'salesEndsAt', 'membershipMode', 'membershipTypeIds', 'bibPrefix', 'bibStartNumber', 'reentry'];

interface TicketForm {
  name: string;
  description: string;
  isAddOn: boolean;
  requiresTicket: boolean;
  addonCategoryId: string | null;
  priceKind: PriceKind;
  price: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  /** null = use the EVENT's rate. ⚠ Not "no tax" — see the picker's `emptyLabel`. */
  taxSettingId: string | null;
  salesMode: string;
  quantityTotal: number | null;
  /** ROADMAP A4 — on a recurring event, the one date this type is sold for (null = every date). */
  eventTimeSlotId: string | null;
  minPerOrder: number | null;
  maxOrderCount: number | null;
  orderIncrement: number | null;
  salesStartsAt: string | null;
  salesEndsAt: string | null;
  membershipMode: string;
  /** The membership types it accepts (settings.membershipTypeIds) — none = any membership. */
  membershipTypeIds: string[];
  bibPrefix: string;
  bibStartNumber: string;
  /** #228 — settings.reentry: 'event' (follow the event's switch — nothing stored), or the type's own allowed / not allowed. */
  reentry: string;
}

/** #228 — the form's "follow the event" choice (stored as no `settings.reentry` at all). */
const REENTRY_SAME_AS_EVENT = 'event';

const reentryOf = (tk: TicketType): string => {
  const v = tk.settings?.reentry;

  return v === TICKET_REENTRY.ALLOWED || v === TICKET_REENTRY.NOT_ALLOWED ? v : REENTRY_SAME_AS_EVENT;
};

/** Read the bib config nested in a ticket type's settings.bib jsonb. */
const bibOf = (tk: TicketType): { prefix: string; startNumber: string } => {
  const bib = (tk.settings?.bib ?? null) as { prefix?: unknown; startNumber?: unknown } | null;

  return {
    prefix: bib?.prefix == null ? '' : String(bib.prefix),
    startNumber: bib?.startNumber == null ? '' : String(bib.startNumber),
  };
};

const priceKindOf = (tk: TicketType): PriceKind => {
  if (tk.isPayWhatYouWant) return PWYW;
  if ((tk.price ?? 0) > 0) return PAID;
  return FREE;
};

const emptyForm = (): TicketForm => ({
  name: '',
  description: '',
  isAddOn: false,
  requiresTicket: false,
  addonCategoryId: null,
  priceKind: PAID,
  price: null,
  taxSettingId: null,
  minPrice: null,
  maxPrice: null,
  salesMode: TICKET_SALES_MODE.ON_SALE,
  quantityTotal: null,
  eventTimeSlotId: null,
  minPerOrder: null,
  maxOrderCount: null,
  orderIncrement: null,
  salesStartsAt: null,
  salesEndsAt: null,
  membershipMode: TICKET_MEMBERSHIP_MODE.ANY,
  membershipTypeIds: [],
  bibPrefix: '',
  bibStartNumber: '',
  reentry: REENTRY_SAME_AS_EVENT,
});

// ⚠ The sales window is a wall clock in the EVENT's zone (ROADMAP #93) — loaded and saved there, never the device's.
const toForm = (tk: TicketType, tz: string | null): TicketForm => ({
  name: tk.name ?? '',
  description: tk.description ?? '',
  isAddOn: !!tk.isAddOn,
  requiresTicket: !!tk.requiresTicket,
  addonCategoryId: tk.addonCategoryId ?? null,
  priceKind: priceKindOf(tk),
  price: tk.price ?? null,
  minPrice: tk.minPrice ?? null,
  maxPrice: tk.maxPrice ?? null,
  taxSettingId: tk.taxSettingId ?? null,
  salesMode: tk.salesMode ?? TICKET_SALES_MODE.ON_SALE,
  quantityTotal: tk.quantityTotal ?? null,
  eventTimeSlotId: tk.eventTimeSlotId ?? null,
  minPerOrder: tk.minPerOrder ?? null,
  maxOrderCount: tk.maxOrderCount ?? null,
  orderIncrement: tk.orderIncrement ?? null,
  salesStartsAt: utcToZonedString(tk.salesStartsAt, tz, false),
  salesEndsAt: utcToZonedString(tk.salesEndsAt, tz, false),
  membershipMode: tk.membershipMode ?? TICKET_MEMBERSHIP_MODE.ANY,
  membershipTypeIds: Array.isArray(tk.settings?.membershipTypeIds) ? (tk.settings?.membershipTypeIds as string[]) : [],
  bibPrefix: bibOf(tk).prefix,
  bibStartNumber: bibOf(tk).startNumber,
  reentry: reentryOf(tk),
});

export interface TicketDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** Event's currency, used as the default for priced tickets. */
  eventCurrency?: string | null;
  ticket: TicketType | null;
  onSaved: () => void;
  /** ROADMAP A4 — a recurring event: quantities are per date, and a type may be sold on one date only. */
  recurring?: boolean;
  /**
   * The event's time zone — names its dates, and the sales window is entered and saved in it (ROADMAP #93). ⚠ Required:
   * without it the window is read in this device's zone.
   */
  timezone: string | null;
  /** A NEW ticket's starting name (the create wizard's "General admission" / "VIP" suggestions). Ignored on edit. */
  defaultName?: string;
}

export const TicketDrawer: React.FC<TicketDrawerProps> = ({
  opened,
  onClose,
  eventId,
  eventCurrency,
  ticket,
  onSaved,
  recurring = false,
  timezone,
  defaultName,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  // The sales window is in the event's zone — said when this device keeps another one.
  const zoneNote = eventZoneNote(t, timezone, 'elsewhere');
  // #A4.7a/b — a ticket is priced in the EVENT's currency: shown on the price inputs, never chosen here.
  const priceAffixes = currencyAffixes(ticket?.currencyCode || eventCurrency);
  const [form, setForm] = useState<TicketForm>(() => emptyForm());
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<OpResult | null>(null);

  // Add-on categories (R16) — group an add-on with similar extras. Managed on the event's Selling
  // settings; only offered on the add-on branch, and only when the host has created at least one.
  const { categories: addonCategories } = useAddonCategories(eventId);
  const { slots } = useEventTimeSlots(opened && recurring ? eventId : null);
  // The bookable dates, plus the one already chosen even if it has passed — so an old restriction still reads.
  const soldOnlyOnOptions = useMemo(() => {
    const options = bookableSlotOptions(slots, timezone);
    const current = slots.find((sl) => sl.id === form.eventTimeSlotId);
    if (current?.id && !options.some((o) => o.value === current.id)) {
      options.unshift({ value: current.id, label: formatSlotLabel(current, timezone) });
    }

    return options;
  }, [slots, timezone, form.eventTimeSlotId]);
  const addonCategoryOptions = useMemo(
    () =>
      addonCategories
        .filter((c) => c.id)
        .map((c) => ({ value: String(c.id), label: c.name || String(c.id) })),
    [addonCategories],
  );

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError],
  );
  const nameRef = useRef<HTMLInputElement>(null);

  // Esc / a click outside / Cancel ask before discarding a changed ticket (#123). A rate made through the nested
  // "Create a tax rate" drawer is picked into the form, so it counts as a change too.
  const { isDirty, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();

      return;
    }

    // A refreshed ticket keeps what the user has typed (#125); another ticket replaces the form.
    const seeded = ticket ? toForm(ticket, timezone) : { ...emptyForm(), name: defaultName ?? '' };
    setForm(reseed(seeded, ticket?.id ?? '__new__'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, ticket, defaultName]);

  useEffect(() => {
    if (!opened) return undefined;
    const id = setTimeout(() => {
      const el = nameRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    }, 120);
    return () => clearTimeout(id);
  }, [opened]);

  const handleChange = useCallback((name: string | undefined, value: unknown) => {
    if (!name) return;
    setForm((f) => {
      // An admission ticket can't "require a ticket"; clear that flag when it
      // stops being an add-on.
      if (name === 'isAddOn' && !value) return { ...f, isAddOn: false, requiresTicket: false };
      // Moving the sales start moves the sales end by the same amount; the host adjusts the end if needed.
      if (name === 'salesStartsAt') {
        const v = (value as string | null) ?? null;

        return { ...f, salesStartsAt: v, salesEndsAt: v ? moveEndWithStart(f.salesStartsAt, f.salesEndsAt, v) : f.salesEndsAt };
      }

      return { ...f, [name]: value };
    });
  }, []);

  const isPwyw = form.priceKind === PWYW;
  const isPaid = form.priceKind === PAID;
  const priced = isPaid || isPwyw;
  // A fixed price of 0 IS a free ticket (a child's place) — owner, 2026-10-17: "some tickets, like for kids can be
  // free". It saves as one (price 0, no tax rate) and reopens as "Free".
  const pricedAtZero = isPaid && form.price === 0;

  const priceKindData = useMemo(
    () => [
      { value: FREE, label: t('events:tickets.kindFree', { defaultValue: 'Free' }) },
      { value: PAID, label: t('events:tickets.kindPaid', { defaultValue: 'Fixed price' }) },
      { value: PWYW, label: t('events:tickets.kindPwyw', { defaultValue: 'Pay what you want' }) },
    ],
    [t],
  );

  const salesModeData = useMemo(
    () => [
      { value: TICKET_SALES_MODE.ON_SALE, label: t('events:tickets.saleOnSale', { defaultValue: 'On sale (online)' }) },
      { value: TICKET_SALES_MODE.DOOR_ONLY, label: t('events:tickets.saleDoor', { defaultValue: 'At the door only' }) },
      { value: TICKET_SALES_MODE.OFF, label: t('events:tickets.saleOff', { defaultValue: 'Not for sale' }) },
    ],
    [t],
  );

  const membershipModeData = useMemo(
    () => [
      { value: TICKET_MEMBERSHIP_MODE.ANY, label: t('events:tickets.memAny', { defaultValue: 'Anyone can buy' }) },
      { value: TICKET_MEMBERSHIP_MODE.OPTIONAL, label: t('events:tickets.memOptional', { defaultValue: 'Members may get a better deal' }) },
      { value: TICKET_MEMBERSHIP_MODE.REQUIRED, label: t('events:tickets.memRequired', { defaultValue: 'Members only' }) },
    ],
    [t],
  );

  // Guardrails: a fixed price needs a value (0 is one — a free ticket); PWYW needs an ordered min/max; the
  // sales window (if both set) must be ordered.
  // ⚠ Until 2026-10-17 a fixed price had to be ABOVE 0, so typing 0 left Save greyed out with nothing saying why, and a
  // free ticket for kids looked impossible to make. The server always took 0 (`rulesOptionalNumber('price', { min: 0 })`).
  const windowOrdered = !form.salesStartsAt || !form.salesEndsAt || new Date(form.salesEndsAt) >= new Date(form.salesStartsAt);
  const pwywOrdered = !isPwyw || form.minPrice == null || form.maxPrice == null || form.maxPrice >= form.minPrice;
  const canSave =
    !!form.name.trim() &&
    (!isPaid || (form.price != null && form.price >= 0)) &&
    pwywOrdered &&
    windowOrdered &&
    !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setSubmitError(null);

    const input: TicketTypeInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      isAddOn: form.isAddOn,
      requiresTicket: form.isAddOn ? form.requiresTicket : false,
      // Category only applies to an add-on; clear it if the type isn't an add-on.
      addonCategoryId: form.isAddOn ? form.addonCategoryId || null : null,
      isPayWhatYouWant: isPwyw,
      // Free → price 0; PWYW → price is the suggested amount (min), not required.
      price: isPaid ? form.price : isPwyw ? form.minPrice ?? 0 : 0,
      minPrice: isPwyw ? form.minPrice : null,
      maxPrice: isPwyw ? form.maxPrice : null,
      // ⚠⚠ THIS FIELD WAS UNREACHABLE. `ticket_type.tax_setting_id` has always been honoured by the
      // server — `TicketOrderCore` reads it first and only falls back to the event's rate — and the
      // SDL and this service both carried it, but NOTHING in the product ever wrote it. So a host
      // who needed one rate for tickets and another for, say, catering add-ons simply could not say
      // so. Declared, agreed on by every layer, and never written: see the memory note of the same
      // name. Cleared for a free ticket, where a rate would be a tax on nothing.
      taxSettingId: priced && !pricedAtZero ? form.taxSettingId || null : null,
      salesMode: form.salesMode,
      quantityTotal: form.quantityTotal,
      eventTimeSlotId: form.eventTimeSlotId || null,
      minPerOrder: form.minPerOrder,
      // ⓘ Not offered for pay what you want (an amount buys the minimum) — cleared, so a value left from before cannot cap it.
      maxOrderCount: isPwyw ? null : form.maxOrderCount,
      orderIncrement: isPwyw ? 1 : form.orderIncrement,
      salesStartsAt: zonedStringToUtc(form.salesStartsAt, timezone),
      salesEndsAt: zonedStringToUtc(form.salesEndsAt, timezone),
      membershipMode: form.membershipMode,
    };

    // BIB config lives in settings.bib — merge it in, preserving other settings keys.
    const bibPrefix = form.bibPrefix.trim();
    const bibStart = form.bibStartNumber.trim();
    const nextSettings: Record<string, unknown> = { ...(ticket?.settings ?? {}) };
    if (bibPrefix || bibStart) {
      nextSettings.bib = { prefix: bibPrefix || null, startNumber: bibStart || null };
    } else {
      delete nextSettings.bib;
    }
    // Memberships (owner, 2026-10-13) — the types a members-only type accepts; none (or "anyone can buy") = any.
    if (form.membershipMode === TICKET_MEMBERSHIP_MODE.REQUIRED && form.membershipTypeIds.length > 0) {
      nextSettings.membershipTypeIds = form.membershipTypeIds;
    } else {
      delete nextSettings.membershipTypeIds;
    }
    // #228 — the type's own re-entry rule; "same as the event" (and any add-on — it is handed out, never re-admitted) stores none.
    if (!form.isAddOn && form.reentry !== REENTRY_SAME_AS_EVENT) {
      nextSettings.reentry = form.reentry;
    } else {
      delete nextSettings.reentry;
    }
    input.settings = nextSettings;

    const result = ticket?.id
      ? await TicketTypeService.update(ticket.id, input)
      : await TicketTypeService.create({ ...input, eventId });

    setSaving(false);
    if (!result.didSucceed()) {
      // Keep the OpResult: field errors go under their inputs, the rest above Save.
      setSubmitError(result);

      return;
    }
    toast.success(t('events:tickets.saved', { defaultValue: 'Ticket saved.' }));
    onSaved();
    onClose();
  };

  // #78 — off while the "Create a tax rate" drawer is open on top: Esc / ⌘S would reach this drawer too.
  const [taxCreating, setTaxCreating] = useState(false);
  // ⚠ This drawer stays mounted between tickets: a flag left on by a picker that vanished would strip the next one of
  // Esc, X and ⌘S.
  useEffect(() => {
    if (!opened) {
      setTaxCreating(false);
    }
  }, [opened]);
  useModalShortcuts({ onConfirm: save, canConfirm: canSave, enabled: opened && !taxCreating });

  const title = ticket
    ? t('events:tickets.editTitle', { defaultValue: 'Edit ticket' })
    : t('events:tickets.addTitle', { defaultValue: 'Add ticket' });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      closable={!taxCreating}
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={save}
      confirmLabel={t('common:buttons.save', { defaultValue: 'Save' })}
      cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      confirmDisabled={!canSave}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <TextField
          ref={nameRef}
          name="name"
          label={t('events:tickets.name', { defaultValue: 'Ticket name' })}
          placeholder={t('events:tickets.namePh', { defaultValue: 'e.g. General Admission' })}
          value={form.name}
          onChange={handleChange}
          required
          error={fieldErrors.name}
        />

        <SwitchField
          name="isAddOn"
          label={t('events:tickets.isAddOn', { defaultValue: 'This is an add-on (not an admission ticket)' })}
          description={t('events:tickets.isAddOnHint', { defaultValue: 'Add-ons are extras like parking, merch or meals.' })}
          checked={form.isAddOn}
          onChange={handleChange}
          error={fieldErrors.isAddOn}
        />
        {form.isAddOn && (
          <SwitchField
            name="requiresTicket"
            label={t('events:tickets.requiresTicket', { defaultValue: 'Can only be bought with an admission ticket' })}
            checked={form.requiresTicket}
            onChange={handleChange}
            error={fieldErrors.requiresTicket}
          />
        )}
        {form.isAddOn && addonCategoryOptions.length > 0 && (
          <SelectField
            name="addonCategoryId"
            label={t('events:tickets.addonCategory', { defaultValue: 'Add-on category' })}
            description={t('events:tickets.addonCategoryHint', {
              defaultValue: 'Group this add-on with similar extras when guests buy (e.g. Food, Merch).',
            })}
            placeholder={t('events:tickets.addonCategoryPh', { defaultValue: 'No category' })}
            data={addonCategoryOptions}
            value={form.addonCategoryId ?? ''}
            onChange={handleChange}
            clearable
            error={fieldErrors.addonCategoryId}
          />
        )}

        <Divider />

        <div>
          <Text fw={600} size="md" mb="xs">
            {t('events:tickets.pricing', { defaultValue: 'Pricing' })}
          </Text>
          <SelectField
            aria-label={t('events:tickets.pricing', { defaultValue: 'Pricing' })}
            name="priceKind"
            data={priceKindData}
            value={form.priceKind}
            onChange={handleChange}
            allowDeselect={false}
            error={fieldErrors.priceKind}
          />

          {priced && (
            <Group gap="sm" mt="sm" wrap="nowrap" align="flex-start">
              {isPaid ? (
                <NumberField
                  name="price"
                  label={t('events:tickets.price', { defaultValue: 'Price' })}
                  description={pricedAtZero ? t('events:tickets.priceZero', { defaultValue: 'A price of 0 makes this ticket free.' }) : undefined}
                  value={form.price}
                  onChange={handleChange}
                  min={0}
                  clampBehavior="strict"
                  decimalScale={2}
                  style={{ flex: 1 }}
                  error={fieldErrors.price}
                  {...priceAffixes}
                />
              ) : (
                <>
                  <NumberField
                    name="minPrice"
                    label={t('events:tickets.minPrice', { defaultValue: 'Minimum' })}
                    value={form.minPrice}
                    onChange={handleChange}
                    min={0}
                    clampBehavior="strict"
                    decimalScale={2}
                    style={{ flex: 1 }}
                    error={fieldErrors.minPrice}
                    {...priceAffixes}
                  />
                  <NumberField
                    name="maxPrice"
                    label={t('events:tickets.maxPrice', { defaultValue: 'Maximum' })}
                    value={form.maxPrice}
                    onChange={handleChange}
                    min={0}
                    clampBehavior="strict"
                    decimalScale={2}
                    error={
                      fieldErrors.maxPrice ||
                      (!pwywOrdered ? t('events:tickets.pwywError', { defaultValue: 'Max must be ≥ min.' }) : undefined)
                    }
                    style={{ flex: 1 }}
                    {...priceAffixes}
                  />
                </>
              )}
            </Group>
          )}

          {/* ⚠ PRICED TYPES ONLY. A tax rate on a free ticket is a tax on nothing, and offering the
              control there invites a host to set one and wonder why no receipt mentions it. */}
          {priced && !pricedAtZero && (
            <TaxSelect
              name="taxSettingId"
              label={t('events:tickets.taxRecord', { defaultValue: 'Tax rate' })}
              description={t('events:tickets.taxRecordHint', {
                defaultValue: "Leave empty to use the event's rate",
              })}
              value={form.taxSettingId}
              onChange={handleChange}
              error={fieldErrors.taxSettingId}
              // ⚠ NOT "No tax". An empty value here means "inherit the event's rate", and the two
              // read the same to a host while charging guests different amounts.
              emptyLabel={t('events:tickets.taxUseEvent', { defaultValue: "Use the event's rate" })}
              onCreatingChange={setTaxCreating}
            />
          )}
        </div>

        <SelectField
          name="salesMode"
          label={t('events:tickets.salesMode', { defaultValue: 'Availability' })}
          data={salesModeData}
          value={form.salesMode}
          onChange={handleChange}
          allowDeselect={false}
          error={fieldErrors.salesMode}
        />

        <Divider />

        <NumberField
          name="quantityTotal"
          label={t('events:tickets.quantityTotal', { defaultValue: 'Quantity available' })}
          description={
            recurring
              ? t('events:tickets.quantityHintPerDate', { defaultValue: 'Available on EACH date. Leave empty for unlimited.' })
              : t('events:tickets.quantityHint', { defaultValue: 'Total inventory. Leave empty for unlimited.' })
          }
          value={form.quantityTotal}
          onChange={handleChange}
          min={0}
          allowDecimal={false}
          placeholder={t('events:tickets.unlimited', { defaultValue: 'Unlimited' })}
          error={fieldErrors.quantityTotal}
        />

        {recurring && (
          <SelectField
            name="eventTimeSlotId"
            label={t('events:tickets.soldOnlyOn', { defaultValue: 'Sold only on' })}
            description={t('events:tickets.soldOnlyOnHint', {
              defaultValue: 'Leave empty to sell this ticket on every date. Pick one for a one-off, e.g. a special night with its own price.',
            })}
            placeholder={t('events:tickets.everyDate', { defaultValue: 'Every date' })}
            data={soldOnlyOnOptions}
            value={form.eventTimeSlotId ?? ''}
            onChange={handleChange}
            clearable
            error={fieldErrors.eventTimeSlotId}
          />
        )}

        {/* Owner, 2026-10-12 — on the event website a pay-what-you-want ticket is an AMOUNT box: one amount buys this
            type's minimum (usually 1). So a maximum per order and "sold in multiples" mean nothing for it — they are not
            offered (and are cleared on save), and the minimum says what it does there. */}
        <Group gap="sm" grow align="flex-start">
          <NumberField
            name="minPerOrder"
            label={
              isPwyw
                ? t('events:tickets.pwywPerAmount', { defaultValue: 'Tickets per amount' })
                : t('events:tickets.minPerOrder', { defaultValue: 'Min per order' })
            }
            description={
              isPwyw
                ? t('events:tickets.pwywPerAmountHint', { defaultValue: 'How many one amount buys — usually 1.' })
                : t('events:tickets.minPerOrderHint', {
                    defaultValue: 'The fewest a guest takes when they choose this type. Min = max sells a fixed number.',
                  })
            }
            value={form.minPerOrder}
            onChange={handleChange}
            min={1}
            allowDecimal={false}
            placeholder="1"
            error={fieldErrors.minPerOrder}
          />
          {!isPwyw && (
            <NumberField
              name="maxOrderCount"
              label={t('events:tickets.maxPerOrder', { defaultValue: 'Max per order' })}
              value={form.maxOrderCount}
              onChange={handleChange}
              min={1}
              allowDecimal={false}
              placeholder={t('events:tickets.noCap', { defaultValue: 'No cap' })}
              error={fieldErrors.maxOrderCount}
            />
          )}
        </Group>

        {!isPwyw && (
          <NumberField
            name="orderIncrement"
            label={t('events:tickets.increment', { defaultValue: 'Sold in multiples of' })}
            description={t('events:tickets.incrementHint', { defaultValue: 'Each order takes a multiple of this: 2 sells 2, 4, 6… (1 = any number).' })}
            value={form.orderIncrement}
            onChange={handleChange}
            min={1}
            allowDecimal={false}
            placeholder="1"
            error={fieldErrors.orderIncrement}
          />
        )}

        <SelectField
          name="membershipMode"
          label={t('events:tickets.membership', { defaultValue: 'Membership' })}
          data={membershipModeData}
          value={form.membershipMode}
          onChange={handleChange}
          allowDeselect={false}
          error={fieldErrors.membershipMode}
        />
        {form.membershipMode === TICKET_MEMBERSHIP_MODE.REQUIRED && (
          <MembershipTypesMultiSelect
            label={t('events:tickets.membershipTypes', { defaultValue: 'Which memberships' })}
            description={t('events:tickets.membershipTypesHint', {
              defaultValue: 'Buyers enter their membership number at checkout. None chosen = any membership.',
            })}
            value={form.membershipTypeIds}
            onChange={(ids) => setForm((f) => ({ ...f, membershipTypeIds: ids }))}
            error={fieldErrors.membershipTypeIds}
          />
        )}
        {form.membershipMode === TICKET_MEMBERSHIP_MODE.OPTIONAL && (
          <Text size="md" c="dimmed">
            {t('events:tickets.membershipOptionalHint', {
              defaultValue: 'Set member prices with Pricing rules (Membership). Buyers enter their membership number at checkout.',
            })}
          </Text>
        )}

        <Divider />

        <Text fw={600} size="md">
          {t('events:tickets.salesWindow', { defaultValue: 'Sales window' })}
        </Text>
        <Text size="sm" c="dimmed" mt={-8}>
          {t('events:tickets.salesWindowHint', { defaultValue: 'Optional — when this ticket can be bought.' })}
        </Text>
        {zoneNote && (
          <Text size="md" c="dimmed" mt={-8}>
            {zoneNote}
          </Text>
        )}
        <Group gap="sm" grow align="flex-start">
          <DateTimeField
            name="salesStartsAt"
            label={t('events:tickets.salesStartsAt', { defaultValue: 'Sales start' })}
            value={form.salesStartsAt}
            onChange={handleChange}
            clearable
            error={fieldErrors.salesStartsAt}
          />
          <DateTimeField
            name="salesEndsAt"
            label={t('events:tickets.salesEndsAt', { defaultValue: 'Sales end' })}
            value={form.salesEndsAt}
            onChange={handleChange}
            notBefore={form.salesStartsAt}
            clearable
            error={
              fieldErrors.salesEndsAt ||
              (!windowOrdered ? t('events:tickets.windowError', { defaultValue: 'End must be after the start.' }) : undefined)
            }
          />
        </Group>

        {/* #228 — re-entry at the door for this type (a day pass yes, a single entry no); add-ons are handed out, not admitted. */}
        {!form.isAddOn ? (
          <SelectField
            name="reentry"
            label={t('events:tickets.reentry', { defaultValue: 'Re-entry' })}
            description={t('events:tickets.reentryHint', { defaultValue: 'Can someone who was checked out with this ticket be checked in again?' })}
            data={[
              { value: REENTRY_SAME_AS_EVENT, label: t('events:tickets.reentryEvent', { defaultValue: "Same as the event's setting" }) },
              { value: TICKET_REENTRY.ALLOWED, label: t('events:tickets.reentryAllowed', { defaultValue: 'Allowed' }) },
              { value: TICKET_REENTRY.NOT_ALLOWED, label: t('events:tickets.reentryNotAllowed', { defaultValue: 'Not allowed' }) },
            ]}
            value={form.reentry}
            onChange={handleChange}
            allowDeselect={false}
            error={fieldErrors.reentry}
          />
        ) : null}

        <Divider />

        <Text fw={600} size="md">
          {t('events:tickets.bibSection', { defaultValue: 'BIB numbers (sport events)' })}
        </Text>
        <Text size="sm" c="dimmed" mt={-8}>
          {t('events:tickets.bibHint', { defaultValue: 'Optional — the numbering used when you generate bibs for this ticket type.' })}
        </Text>
        <Group gap="sm" grow align="flex-start">
          <TextField
            name="bibPrefix"
            label={t('events:tickets.bibPrefix', { defaultValue: 'BIB prefix' })}
            placeholder={t('events:tickets.bibPrefixPh', { defaultValue: 'e.g. MSP, 5K' })}
            value={form.bibPrefix}
            onChange={handleChange}
            error={fieldErrors.bibPrefix}
            maxLength={32}
          />
          <TextField
            name="bibStartNumber"
            label={t('events:tickets.bibStart', { defaultValue: 'Start number' })}
            description={t('events:tickets.bibStartHint', { defaultValue: 'Leading zeros set the digit width, e.g. 0001.' })}
            placeholder="0001"
            value={form.bibStartNumber}
            onChange={handleChange}
            error={fieldErrors.bibStartNumber}
            maxLength={16}
          />
        </Group>

        <TextAreaField
          name="description"
          label={t('events:tickets.description', { defaultValue: 'Description' })}
          description={t('events:tickets.descriptionHint', { defaultValue: 'Optional — shown to buyers on the ticket.' })}
          value={form.description}
          onChange={handleChange}
          autosize
          minRows={2}
          maxRows={4}
          error={fieldErrors.description}
        />
      </Stack>
    </AppDrawer>
  );
};
